import { useEffect, useState } from "react";
import {
	getDefaultAllowedDomains,
	updateEnvironmentSettings,
	type DomainEgressMode,
	type Environment,
	type EnvironmentSettingsInput,
} from "@/lib/api";
import { domainSyntaxError } from "@/lib/domain-validation";
import { InputField } from "@/components/common/InputField";
import { EnvironmentSettingsForm } from "./EnvironmentSettingsForm";
import { Icon } from "@/components/common/Icon";
import { ACTION_LINK_CLASS } from "@/lib/link-styles";
import { INPUT_STYLE, focusRingOn, focusRingOff } from "@/lib/form-styles";

interface EnvironmentSettingsPanelProps {
	/** 設定対象の環境。 */
	environment: Environment;
	/** 保存成功時に更新後の環境を通知する。 */
	onSaved: (updated: Environment) => void;
}

/** 構文エラー時に入力欄へ適用する赤系のリング。 */
const ERROR_RING = "0 0 0 1px var(--danger-solid)";

export function EnvironmentSettingsPanel({ environment, onSaved }: EnvironmentSettingsPanelProps) {
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);
	const [loading, setLoading] = useState(false);

	// 許可ドメイン: 「既定のリストを使用」(null で送る)・「カスタムリストを指定」(1 行 1 ドメインの
	// テキストで編集し、配列で送る)・「制限なし」(allowAll で送る) の 3 状態。
	const [domainsMode, setDomainsMode] = useState<"default" | "custom" | "unrestricted">(
		environment.domainEgressMode === "allowAll"
			? "unrestricted"
			: environment.domainEgressMode === "allowList"
				? "custom"
				: "default",
	);
	const [domainsText, setDomainsText] = useState(environment.allowedDomains?.join("\n") ?? "");
	const [domainsError, setDomainsError] = useState<string | null>(null);

	// 組み込み既定リスト。既定モードでの表示と、カスタムへ切り替えた際の初期値に使う。
	const [defaultDomains, setDefaultDomains] = useState<string[] | null>(null);
	const [defaultDomainsError, setDefaultDomainsError] = useState<string | null>(null);

	// 既定の許可ドメインを取得する (既定モードでの表示とカスタムへの切り替え時の初期値に使う)。
	// 取得失敗はインラインで表面化させる。
	useEffect(() => {
		let cancelled = false;
		setDefaultDomainsError(null);
		getDefaultAllowedDomains()
			.then((domains) => {
				if (!cancelled) setDefaultDomains(domains);
			})
			.catch((err: Error) => {
				if (!cancelled) setDefaultDomainsError(err.message);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	function validate(): EnvironmentSettingsInput | null {
		// カスタム (allowList) は 1 行 1 ドメインとして解釈し、バックエンドと同じ規則で検証する。
		// 空行を除いた結果が空でも、明示的な全遮断 ([]) として有効。既定・制限なしは allowedDomains を
		// 送らず (null)、モードだけで表現する。
		let allowedDomains: string[] | null = null;
		if (domainsMode === "custom") {
			const lines = domainsText
				.split("\n")
				.map((line) => line.trim())
				.filter((line) => line.length > 0);
			const invalidDomain = lines.find((line) => domainSyntaxError(line) !== null);
			if (invalidDomain !== undefined) {
				setDomainsError(`許可ドメインの形式が不正です: ${invalidDomain}`);
				return null;
			}
			allowedDomains = lines;
		}
		setDomainsError(null);
		const domainEgressMode: DomainEgressMode =
			domainsMode === "unrestricted"
				? "allowAll"
				: domainsMode === "custom"
					? "allowList"
					: "allowDefault";
		return { domainEgressMode, allowedDomains };
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		const validated = validate();
		if (!validated) return;
		setSubmitError(null);
		setSaved(false);
		setLoading(true);
		try {
			const updated = await updateEnvironmentSettings(environment.id, validated);
			onSaved(updated);
			setSaved(true);
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	return (
		<EnvironmentSettingsForm
			testId="environment-settings-panel"
			onSubmit={handleSubmit}
			loading={loading}
			saved={saved}
			submitError={submitError}
		>
			<InputField
				label="許可ドメイン - エージェントからの外向き通信 (HTTP/HTTPS) を許可するドメインです。"
				error={domainsError ?? undefined}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
						<label
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 6,
								fontSize: "var(--text-ui)",
								color: "var(--fg-2)",
								fontFamily: "var(--font-sans)",
								cursor: "pointer",
							}}
						>
							<input
								type="radio"
								name="environment-settings-domains-mode"
								checked={domainsMode === "default"}
								onChange={() => setDomainsMode("default")}
								data-testid="environment-settings-domains-mode-default"
							/>
							既定のリストを使用
						</label>
						<label
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 6,
								fontSize: "var(--text-ui)",
								color: "var(--fg-2)",
								fontFamily: "var(--font-sans)",
								cursor: "pointer",
							}}
						>
							<input
								type="radio"
								name="environment-settings-domains-mode"
								checked={domainsMode === "custom"}
								onChange={() => {
									setDomainsMode("custom");
									// 白紙から書かせず、既定リストを出発点として編集できるようにする。
									if (domainsText.trim().length === 0 && defaultDomains) {
										setDomainsText(defaultDomains.join("\n"));
									}
								}}
								data-testid="environment-settings-domains-mode-custom"
							/>
							カスタムリストを指定
							<span
								data-testid="environment-settings-domains-info"
								title="1行に1ドメインを指定します。カスタムリストは既定のリストを完全に置き換えます。エージェント内で apt-get を使うにはdebian.orgと*.debian.orgの許可が必要です。"
								data-tooltip-delay={0}
								style={{ display: "inline-flex", color: "var(--fg-2)", cursor: "help" }}
							>
								<Icon name="info" size={14} />
							</span>
						</label>
						<label
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 6,
								fontSize: "var(--text-ui)",
								color: "var(--fg-2)",
								fontFamily: "var(--font-sans)",
								cursor: "pointer",
							}}
						>
							<input
								type="radio"
								name="environment-settings-domains-mode"
								checked={domainsMode === "unrestricted"}
								onChange={() => setDomainsMode("unrestricted")}
								data-testid="environment-settings-domains-mode-unrestricted"
							/>
							制限なし
							<span
								data-testid="environment-settings-domains-unrestricted-info"
								title="すべてのドメインへの外向き通信 (HTTP/HTTPS) を許可します。"
								data-tooltip-delay={0}
								style={{ display: "inline-flex", color: "var(--fg-2)", cursor: "help" }}
							>
								<Icon name="info" size={14} />
							</span>
						</label>
					</div>
					{defaultDomainsError && (
						<span
							style={{
								fontSize: "var(--text-caption)",
								color: "var(--danger)",
								fontFamily: "var(--font-sans)",
							}}
						>
							{defaultDomainsError}
						</span>
					)}
					{domainsMode === "default" ? (
						defaultDomains && (
							<pre
								data-testid="environment-settings-domains-default-list"
								style={{
									margin: 0,
									boxSizing: "border-box",
									padding: "8px 10px",
									height: 150,
									overflowY: "auto",
									background: "var(--surface-2)",
									borderRadius: "var(--radius-3)",
									fontSize: "var(--text-caption)",
									lineHeight: "var(--leading-body)",
									color: "var(--fg-2)",
									fontFamily: "var(--font-mono)",
								}}
							>
								{defaultDomains.join("\n")}
							</pre>
						)
					) : domainsMode === "unrestricted" ? (
						<div
							data-testid="environment-settings-domains-unrestricted-note"
							style={{
								boxSizing: "border-box",
								padding: "12px 14px",
								background: "var(--surface-2)",
								borderRadius: "var(--radius-3)",
								fontSize: "var(--text-caption)",
								lineHeight: "var(--leading-body)",
								color: "var(--fg-2)",
								fontFamily: "var(--font-sans)",
							}}
						>
							すべてのドメインへの外向き通信 (HTTP/HTTPS) を許可します。
						</div>
					) : (
						<div style={{ position: "relative" }}>
							<textarea
								value={domainsText}
								onChange={(e) => setDomainsText(e.target.value)}
								placeholder={"例:\nexample.com\n*.example.com"}
								spellCheck={false}
								data-testid="environment-settings-domains-textarea"
								style={{
									...INPUT_STYLE,
									display: "block",
									height: 150,
									padding: "8px 10px",
									fontFamily: "var(--font-mono)",
									fontSize: "var(--text-caption)",
									lineHeight: "var(--leading-body)",
									resize: "vertical",
									...(domainsError !== null ? { boxShadow: ERROR_RING } : {}),
								}}
								onFocus={(e) => focusRingOn(e.currentTarget)}
								onBlur={(e) => {
									// 通常のリング復帰では構文エラーの赤リングが消えてしまうため、
									// エラー中はエラーリングへ戻す。
									if (domainsError !== null) {
										e.currentTarget.style.boxShadow = ERROR_RING;
									} else {
										focusRingOff(e.currentTarget);
									}
								}}
							/>
							<button
								type="button"
								data-testid="environment-settings-domains-restore"
								disabled={!defaultDomains}
								onClick={() => setDomainsText((defaultDomains ?? []).join("\n"))}
								className={ACTION_LINK_CLASS}
								style={{ position: "absolute", top: 8, right: 18 }}
							>
								既定値に戻す
							</button>
						</div>
					)}
				</div>
			</InputField>
		</EnvironmentSettingsForm>
	);
}
